export type Force = 'MUST' | 'NEVER' | 'SHOULD'

export type AtomRow = {
  force: Force
  rule: string
  trigger: string
  source: string
  words: string
  /** e.g. "conflicts with #7" */
  note?: string
}

export type Sample = {
  id: string
  badge: string
  name: string
  meta: string
  /** Shown on the right of the ledger header. */
  file: string
  total: number
  binding: number
  conflicts: number
  rows: AtomRow[]
}

export const SAMPLES: Sample[] = [
  {
    id: 'hipaa',
    badge: 'REG',
    name: 'HIPAA Privacy Rule',
    meta: 'regulation · excerpt, 14 pages',
    file: 'HIPAA_Privacy_Rule_164.pdf · 14 pages · read in 2.6s',
    total: 38,
    binding: 31,
    conflicts: 0,
    rows: [
      { force: 'NEVER', rule: 'Disclose PHI without authorization outside treatment, payment, or operations', trigger: 'patient identifier', source: '§164.502(a)', words: '“may not use or disclose protected health information, except”' },
      { force: 'MUST', rule: 'Limit PHI to the minimum necessary for the purpose', trigger: 'PHI in a request', source: '§164.502(b)', words: '“make reasonable efforts to limit protected health information to the minimum necessary”' },
      { force: 'MUST', rule: 'Verify the identity of anyone requesting PHI', trigger: 'records request', source: '§164.514(h)', words: '“verify the identity of a person requesting protected health information”' },
      { force: 'MUST', rule: 'Give patients access to their records within 30 days', trigger: 'access request', source: '§164.524(b)', words: '“no later than 30 days after receipt of the request”' },
      { force: 'NEVER', rule: 'Sell PHI without written authorization', trigger: 'data sharing', source: '§164.502(a)(5)(ii)', words: '“a covered entity may not sell protected health information”' },
      { force: 'MUST', rule: 'Get a written business associate agreement before sharing PHI with a vendor', trigger: 'vendor onboarding', source: '§164.502(e)', words: '“may disclose protected health information to a business associate”' },
      { force: 'MUST', rule: 'Account for disclosures on request, going back six years', trigger: 'accounting request', source: '§164.528(a)', words: '“an accounting of disclosures … in the six years prior”' },
    ],
  },
  {
    id: 'finra',
    badge: 'REG',
    name: 'FINRA Rule 2210',
    meta: 'regulation · 3,100 words',
    file: 'FINRA_Rule_2210.txt · 3,100 words · read in 1.4s',
    total: 27,
    binding: 19,
    conflicts: 0,
    rows: [
      { force: 'MUST', rule: 'Communications must be fair, balanced, and not misleading', trigger: 'retail communication', source: '§2210(d)(1)(A)', words: '“fair and balanced, and must provide a sound basis”' },
      { force: 'NEVER', rule: 'Predict or project performance', trigger: 'performance language', source: '§2210(d)(1)(F)', words: '“may not predict or project performance”' },
      { force: 'NEVER', rule: 'Omit a material fact that makes the communication misleading', trigger: 'retail communication', source: '§2210(d)(1)(A)', words: '“no member may omit any material fact”' },
      { force: 'MUST', rule: 'Have a registered principal approve retail communications before use', trigger: 'retail communication', source: '§2210(b)(1)(A)', words: '“an appropriately qualified registered principal must approve”' },
      { force: 'MUST', rule: 'Disclose the member’s name in retail communications', trigger: 'retail communication', source: '§2210(d)(3)', words: '“must prominently disclose the name of the member”' },
      { force: 'NEVER', rule: 'Make exaggerated, unwarranted, or promissory claims', trigger: 'any claim', source: '§2210(d)(1)(B)', words: '“exaggerated, unwarranted, promissory or misleading”' },
      { force: 'MUST', rule: 'Keep retail communications for three years', trigger: 'retail communication', source: '§2210(b)(4)(A)', words: '“retained for a period of at least three years”' },
    ],
  },
  {
    id: 'fda',
    badge: 'PDF',
    name: 'FDA risk guidance',
    meta: 'guidance · 20 pages',
    file: 'FDA_Presenting_Risk_Info.pdf · 20 pages · read in 3.3s',
    total: 33,
    binding: 14,
    conflicts: 1,
    rows: [
      { force: 'MUST', rule: 'Present risk information with comparable prominence to benefit', trigger: 'promotional claim', source: 'p.6 II.A', words: '“reasonably comparable in scope and prominence”' },
      { force: 'NEVER', rule: 'Minimize risk with tone, framing, or placement', trigger: 'risk statement', source: 'p.9 II.C', words: '“should not minimize the risks”' },
      { force: 'MUST', rule: 'Include the most serious risks in the main body, not a footnote', trigger: 'promotional piece', source: 'p.12 III.B', words: '“not relegated to a footnote or small type”' },
      { force: 'MUST', rule: 'Use language a consumer can understand for consumer pieces', trigger: 'consumer audience', source: 'p.14 III.D', words: '“in consumer-friendly language”' },
      { force: 'SHOULD', rule: 'Match the specificity of risk claims to the specificity of benefit claims', trigger: 'efficacy claim', source: 'p.10 II.D', words: '“specific benefit claims should be accompanied by”', note: 'conflicts with #7' },
      { force: 'NEVER', rule: 'Present efficacy without a corresponding risk disclosure', trigger: 'efficacy claim', source: 'p.7 II.B', words: '“efficacy claims cannot be presented without risk”' },
      { force: 'MUST', rule: 'Quote the indication as approved', trigger: 'indication', source: 'p.8 II.B', words: '“consistent with the approved labeling”' },
    ],
  },
  {
    id: 'travel',
    badge: 'PDF',
    name: 'Travel & expense SOP',
    meta: 'policy · 11 pages',
    file: 'Travel_Expense_Policy_v4.pdf · 11 pages · read in 2.1s',
    total: 41,
    binding: 9,
    conflicts: 2,
    rows: [
      { force: 'MUST', rule: 'Submit receipts for any expense over $25', trigger: 'expense submitted', source: 'p.3 §2.1', words: '“itemized receipts are required for all expenses exceeding $25”' },
      { force: 'NEVER', rule: 'Book first class on domestic flights', trigger: 'flight booking', source: 'p.4 §3.2', words: '“first class is not permitted on domestic travel”' },
      { force: 'MUST', rule: 'Get VP approval for international travel before booking', trigger: 'international trip', source: 'p.4 §3.4', words: '“prior written approval from a Vice President”' },
      { force: 'SHOULD', rule: 'Book flights 14 or more days in advance', trigger: 'flight booking', source: 'p.4 §3.1', words: '“employees are encouraged to book at least two weeks ahead”' },
      { force: 'MUST', rule: 'Submit expenses within 30 days of the trip', trigger: 'trip ended', source: 'p.6 §5.1', words: '“within thirty (30) days of return”', note: 'conflicts with #9' },
      { force: 'NEVER', rule: 'Expense alcohol without a client present', trigger: 'meal expense', source: 'p.5 §4.3', words: '“alcohol is reimbursable only when entertaining clients”' },
      { force: 'MUST', rule: 'Use the corporate card where accepted', trigger: 'payment', source: 'p.2 §1.4', words: '“the corporate card must be used for all business expenses where accepted”' },
    ],
  },
  {
    id: 'brand',
    badge: 'DOC',
    name: 'Brand voice guide',
    meta: 'brand book · 22 pages',
    file: 'Brand_Voice_Guide_2026.docx · 22 pages · read in 2.9s',
    total: 29,
    binding: 6,
    conflicts: 1,
    rows: [
      { force: 'MUST', rule: 'Write the product name with the registered mark on first mention', trigger: 'product mention', source: 'p.4 Naming', words: '“Veltrix® on first use in any document”' },
      { force: 'NEVER', rule: 'Use superlatives about efficacy', trigger: 'efficacy claim', source: 'p.9 Claims', words: '“no “best,” “fastest,” “most effective””' },
      { force: 'SHOULD', rule: 'Open with the patient, not the product', trigger: 'patient-facing copy', source: 'p.6 Voice', words: '“lead with the person; the product follows”' },
      { force: 'NEVER', rule: 'Use the word “cure”', trigger: 'any claim', source: 'p.9 Claims', words: '“we do not use “cure” in any context”' },
      { force: 'MUST', rule: 'Address HCPs formally: title and surname', trigger: 'HCP audience', source: 'p.12 Audiences', words: '“Dr. Patel, never Anita”' },
      { force: 'SHOULD', rule: 'Keep sentences under 20 words in consumer materials', trigger: 'consumer audience', source: 'p.7 Voice', words: '“short sentences; one idea each”', note: 'conflicts with #7' },
      { force: 'MUST', rule: 'Include the tagline on every external piece', trigger: 'external piece', source: 'p.15 Lockups', words: '“the tagline appears with the logo on all external materials”' },
    ],
  },
  {
    id: 'slack',
    badge: 'SLACK',
    name: '#med-info Slack thread',
    meta: 'conversation · 212 messages',
    file: '#med-info-reviews · Aug 14 – Sep 9 · 212 messages · read in 1.8s',
    total: 19,
    binding: 11,
    conflicts: 1,
    rows: [
      { force: 'MUST', rule: 'Run “quick” replies through review anyway', trigger: 'request marked quick', source: 'D. Okafor · Sep 3, 2:14 PM', words: '“quick doesn’t mean skip review, it means I need it today”' },
      { force: 'NEVER', rule: 'Send dosing numbers from memory', trigger: 'dosing', source: 'M. Rao · Aug 21, 9:40 AM', words: '“copy it from the label. every time. I don’t care if you know it”' },
      { force: 'MUST', rule: 'Cc the MSL on anything from an oncologist', trigger: 'HCP specialty: oncology', source: 'P. Nair · Aug 27, 4:02 PM', words: '“if it’s onc, loop in Sunita before it goes out”' },
      { force: 'SHOULD', rule: 'Answer in two paragraphs, citations at the end', trigger: 'HCP reply', source: 'D. Okafor · Aug 18, 11:15 AM', words: '“two paras, cites at the bottom, that’s the house style now”' },
      { force: 'NEVER', rule: 'Mention samples in a med-info reply', trigger: 'HCP reply', source: 'M. Rao · Sep 1, 3:31 PM', words: '“samples is a sales word. it never appears in our replies”' },
      { force: 'MUST', rule: 'Log every HCP inquiry in the tracker before replying', trigger: 'HCP inquiry', source: 'P. Nair · Aug 29, 10:06 AM', words: '“tracker first, reply second. no exceptions”' },
      { force: 'SHOULD', rule: 'Reply within one business day', trigger: 'HCP inquiry', source: 'D. Okafor · Sep 5, 8:52 AM', words: '“a day is the promise we made them”', note: 'conflicts with #1' },
    ],
  },
  {
    id: 'interviews',
    badge: 'AUDIO',
    name: 'Team interviews',
    meta: 'transcripts · 4 reviewers, 3h 10m',
    file: 'Reviewer_Interviews_Sept.mp3 × 4 · 3h 10m · read in 6.4s',
    total: 46,
    binding: 17,
    conflicts: 2,
    rows: [
      { force: 'MUST', rule: 'Lead with the downside for clients who carried losses in 2008', trigger: 'named client with loss history', source: 'M. Rao, senior reviewer · 00:14:22', words: '“with those clients you say the bad news first, always. they read the second sentence looking for it”' },
      { force: 'NEVER', rule: 'Send a figure before the custodian statement posts', trigger: 'performance figure', source: 'M. Rao · 00:21:05', words: '“if my number and the custodian’s number differ by a basis point, I’ve lost that client”' },
      { force: 'MUST', rule: 'Name the section of the policy in every denial', trigger: 'denial letter', source: 'J. Lin, claims lead · 00:08:47', words: '“no section, no denial. the regulator asks for it every single time”' },
      { force: 'SHOULD', rule: 'Offer a call before the second written reply', trigger: 'second reply on same issue', source: 'J. Lin · 00:31:10', words: '“by the second letter they’re angry. pick up the phone”', note: 'conflicts with #6' },
      { force: 'NEVER', rule: 'Use “unfortunately” in a denial', trigger: 'denial letter', source: 'S. Park, compliance · 00:05:33', words: '“it reads as sarcasm. we struck it in 2019”' },
      { force: 'MUST', rule: 'Attach the inspection report when citing it', trigger: 'citing a report', source: 'J. Lin · 00:19:58', words: '“if you mention the report, they get the report”' },
      { force: 'MUST', rule: 'Route anything with a lawyer cc’d to legal before replying', trigger: 'counsel copied', source: 'S. Park · 00:12:40', words: '“the moment there’s a lawyer on the thread, it’s not our letter anymore”' },
    ],
  },
  {
    id: 'meeting',
    badge: 'NOTES',
    name: 'Dept. head meeting',
    meta: 'meeting notes · Sep 12, 48 min',
    file: 'UM_Committee_Sep12_notes.docx · 48 min · read in 1.2s',
    total: 14,
    binding: 9,
    conflicts: 0,
    rows: [
      { force: 'MUST', rule: 'Hold member notice until a clinician signs the denial', trigger: 'adverse determination', source: 'Dr. A. Mensah · decision, 00:09', words: '“nothing adverse leaves without my signature or Priya’s”' },
      { force: 'NEVER', rule: 'Auto-close on CPT 72148', trigger: 'CPT 72148', source: 'Dr. A. Mensah · decision, 00:17', words: '“lumbar MRI denials are where we lose appeals. a human reads every one”' },
      { force: 'MUST', rule: 'Cite the InterQual criteria set and version on every denial', trigger: 'adverse determination', source: 'action item · 00:22', words: '“criteria and version number, in the letter, every time”' },
      { force: 'MUST', rule: 'Offer peer-to-peer within 24 hours of a denial', trigger: 'adverse determination', source: 'Dr. A. Mensah · decision, 00:26', words: '“offer the call before they ask for it”' },
      { force: 'SHOULD', rule: 'Flag any case over 30 days old to the committee', trigger: 'case age > 30 days', source: 'action item · 00:34', words: '“thirty days is the line. flag it, don’t sit on it”' },
      { force: 'NEVER', rule: 'Reference cost in an adverse determination', trigger: 'adverse determination', source: 'Dr. A. Mensah · decision, 00:39', words: '“the letter is about medical necessity. cost is not in it. ever”' },
      { force: 'MUST', rule: 'Send the member notice and the provider notice the same day', trigger: 'adverse determination', source: 'action item · 00:44', words: '“same day, both. no gap where one knows and the other doesn’t”' },
    ],
  },
]

/** The ledger never shows more than this many rows. */
export const MAX_ROWS = 7
